import { useRef, useEffect } from 'react'
import { useHistory, matchPath } from 'react-router'
import { Location } from 'history'

interface LocationState {
  goBackIndex?: number
  [key: string]: any
}

export const useBackButtonHistory = ({ rules }: { rules?: string[] }) => {
  const history = useHistory<LocationState>()
  const location = history.location
  const locationRef = useRef<Location<LocationState>>(location)

  useEffect(() => {
    const unlisten = history.listen((nextLocation, action) => {
      const prevLocation = locationRef.current

      // avoid infinite redirects
      if (
        nextLocation.pathname === prevLocation.pathname &&
        action === 'REPLACE'
      ) {
        return
      }

      // keep correct locationRef state on back/forward browser buttons navigation
      if (action === 'POP') {
        locationRef.current = nextLocation
        return
      }

      const { state: { goBackIndex: currentGoBackIndex = -1 } = {} } =
        prevLocation
      const matchPrev = matchPath(prevLocation.pathname, {
        path: rules,
        exact: true,
        strict: false
      })
      const matchNext = matchPath(nextLocation.pathname, {
        path: rules,
        exact: true,
        strict: false
      })

      // catch behavior of Redirect component or history.replace method
      if (action === 'REPLACE' && matchPrev?.path === matchNext?.path) {
        if (!nextLocation.state) {
          nextLocation.state = prevLocation.state
        } else {
          nextLocation.state.goBackIndex = prevLocation.state.goBackIndex
        }
      }

      if (action === 'PUSH') {
        // check that they are from the same path
        const goBackIndex =
          matchPrev && matchNext && matchPrev?.path === matchNext?.path
            ? currentGoBackIndex - 1
            : -1

        if (!nextLocation.state) {
          nextLocation.state = { goBackIndex }
        } else {
          nextLocation.state.goBackIndex = goBackIndex
        }
      }

      // update next location
      locationRef.current = nextLocation
      history.replace(nextLocation)
    })

    return unlisten
  }, [])
}
